import { Component, Injector, OnInit, ViewChild } from "@angular/core";
import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { Vision, VisionStatus } from "@common/ADAPT.Common.Model/organisation/vision";
import { VisionCategory } from "@common/ADAPT.Common.Model/organisation/vision-category";
import { ImplementationKitService } from "@common/implementation-kit/implementation-kit.service";
import { ImplementationKitArticle } from "@common/implementation-kit/implementation-kit-article.enum";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ChatAiService } from "@common/lib/chat-ai/chat-ai.service";
import { StringUtilities } from "@common/lib/utilities/string-utilities";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";
import { buttonPreset } from "@common/ux/button/button-preset";
import { ChangeAction, ChangeManagerService } from "@common/ux/change-manager/change-manager.service";
import { IAdaptMenuItem } from "@common/ux/menu/menu.component";
import { OrganisationPageRouteBuilder } from "@org-common/lib/route/organisation-page-route-builder";
import { ContentReadyEvent, SelectionChangedEvent } from "devextreme/ui/list";
import { DxListComponent } from "devextreme-angular";
import { distinctUntilChanged, EMPTY, finalize, forkJoin, map, Observable, of, switchMap, tap } from "rxjs";
import { VisionService } from "../vision.service";
import { VisionAuthService } from "../vision-auth.service";

export const CategoryIdParamKey = "categoryId";

@Component({
    selector: "adapt-edit-vision-page",
    standalone: false,
    templateUrl: "./edit-vision-page.component.html",
    styleUrl: "./edit-vision-page.component.scss",
})
export class EditVisionPageComponent extends BaseRoutedComponent implements OnInit {
    public readonly SummaryCategory = {
        visionCategoryId: 0,
        name: "Summary",
        description: "Summary of all the visions or just the overview paragraph",
    } as VisionCategory;

    @ViewChild(DxListComponent) private categoryListComponent?: DxListComponent;

    public categories?: VisionCategory[];
    public selectedCategory?: VisionCategory;
    public visions: Vision[] = [];
    public reviewResult?: string;
    public teamId?: number;
    public personId?: number;

    public editableVision?: Vision;
    public ownersVisions?: Vision[];
    public hideGuidance = false;

    public reviewStatementMenuItem: IAdaptMenuItem = {
        text: "Review statement",
        icon: "fal fa-fw fa-magnifying-glass-waveform",
        onClick: () => this.setBusyWhile(this.reviewVisionStatement(this.editableVision!)).subscribe(() => this.scrollRightColumnToBottom()),
    };
    public improveClarityGrammarMenuItem: IAdaptMenuItem = {
        text: "Improve clarity and grammar",
        icon: "fal fa-fw fa-spell-check",
        onClick: () => this.setBusyWhile(this.improveClarityGrammar(this.editableVision!)).subscribe(() => this.scrollRightColumnToBottom()),
    };
    public integratePersonalStatementsMenuItem: IAdaptMenuItem = {
        text: "Integrate owner's statements",
        icon: "fal fa-fw fa-object-exclude",
        onClick: () => this.setBusyWhile(this.integrateOwnersVisions()).subscribe(() => this.scrollRightColumnToBottom()),
    };
    public summariseIntegratedVisionMenuItem: IAdaptMenuItem = {
        text: "Summarise integrated vision statements",
        icon: "fal fa-fw fa-list-check",
        onClick: () => this.setBusyWhile(this.generateIntegratedSummary()).subscribe(() => this.scrollRightColumnToBottom()),
    };
    public aiHelperMenuItems = [{
        text: "",
        icon: buttonPreset.wizardLink.iconClass,
        items: [
            this.reviewStatementMenuItem,
            this.improveClarityGrammarMenuItem,
            this.integratePersonalStatementsMenuItem,
            this.summariseIntegratedVisionMenuItem,
        ],
    }] as IAdaptMenuItem[];
    public aiBusy = false;

    private summaryIntegratedVision?: Vision;

    // setting selected category directly from dxList selection is causing ExpressionChangedAfterItHasBeenCheckedError
    private setSelectedCategory = this.createThrottledUpdater((category: VisionCategory) => {
        this.selectedCategory = category;
        this.editableVision = this.getEditableVisionForCategory(category.visionCategoryId);
        this.integratePersonalStatementsMenuItem.visible = !(this.editableVision?.extensions.isPersonal || this.editableVision?.extensions.isSummary);
        this.summariseIntegratedVisionMenuItem.visible = this.editableVision?.extensions.isSummary;
        this.checkContentActionMenu();
        if (this.editableVision?.extensions.isIntegrated && !this.editableVision?.extensions.isSummary) {
            this.ownersVisions = this.getOwnersVisionsForCategory(category.visionCategoryId);
            this.hideGuidance = true;
        } else {
            this.ownersVisions = undefined;
            this.hideGuidance = false;
        }
    });

    public constructor(
        injector: Injector,
        private visionService: VisionService,
        private visionAuthService: VisionAuthService,
        private changeManagerService: ChangeManagerService,
        private chatAiService: ChatAiService,
        public ikService: ImplementationKitService,
    ) {
        super(injector);
    }

    public get hasChanges() {
        return this.visions.some((v) => !v.entityAspect.entityState.isUnchanged());
    }

    public get hasIncompleteCategory() {
        if (!this.visions.length) {
            return false;
        }

        return this.visions.some((v) => (this.personId ? v.extensions.isPersonal : v.extensions.isIntegrated) &&
            (!this.personId || this.personId === v.personId) && !v.extensions.isCompleted);
    }

    public ngOnInit() {
        this.teamId = this.getRouteParamInt("teamId");
        this.personId = this.getRouteParamInt("personId");
        if (this.teamId) {
            const getVisions$ = this.personId
                ? this.visionService.getVisionsByPersonTeam(this.personId, this.teamId)
                : this.visionService.getVisionsByTeam(this.teamId).pipe(
                    map((visions) => visions.sort(this.visionService.defaultVisionSortFunction))
                );
            forkJoin([
                this.visionService.getVisionCategoriesForTeam(this.teamId),
                getVisions$,
                this.visionService.getSummaryIntegratedVisionByTeam(this.teamId),
            ]).pipe(
                this.takeUntilDestroyed(),
            ).subscribe(([categories, visions, summaryVision]) => {
                this.categories = categories;
                this.visions = visions;
                this.summaryIntegratedVision = summaryVision;
                if (visions.some((v) => !v.visionCategoryId)) {
                    // summary without category
                    if (this.categories && !this.categories.includes(this.SummaryCategory)) {
                        this.categories.push(this.SummaryCategory);
                    }
                }

                this.notifyActivated();
                this.shellUiService.removeDefaultShellPadding();

                this.verifyHasAccessToRoute(this.visionAuthService.canEditVisionForTeamId(this.teamId!));
            });
        }

        this.setSelectedCategory.pipe(
            distinctUntilChanged(),
            this.takeUntilDestroyed(),
        ).subscribe((category) => this.setSearchParameterValue(CategoryIdParamKey, category.visionCategoryId.toString()));
    }

    public getImplementationKitArticle(slugString: string) {
        return slugString as ImplementationKitArticle;
    }

    public onCategoryListReady(e: ContentReadyEvent) {
        if (e.component && this.categories?.length) {
            const selectedCategoryId = this.getSearchParameterIntValue(CategoryIdParamKey);
            if (selectedCategoryId !== undefined && !isNaN(selectedCategoryId)) {
                const categoryToSelect = this.categories.find((c) => c.visionCategoryId === selectedCategoryId) ?? this.categories[0];
                e.component.selectItem(this.categories.indexOf(categoryToSelect));
            } else if (this.categories.length) {
                // find first incomplete category
                const firstIncompleteCategory = this.categories.find((c) => {
                    const vision = this.getEditableVisionForCategory(c.visionCategoryId);
                    return vision && !vision.extensions.isCompleted;
                });
                const selectedIndex = firstIncompleteCategory
                    ? this.categories.indexOf(firstIncompleteCategory)
                    : 0;
                e.component.selectItem(selectedIndex);
            }
        }
    }

    public onCategorySelected(e: SelectionChangedEvent) {
        this.reviewResult = undefined;
        if (e.addedItems && e.addedItems.length > 0) {
            const newCategorySelection = e.addedItems[0];
            if (this.hasChanges && this.selectedCategory !== newCategorySelection) {
                const lastSelectedCategory = this.selectedCategory;
                this.changeManagerService.promptForUnsavedChanges().pipe(
                    switchMap((result) => {
                        const changedVisions = this.visions.filter((v) => !v.entityAspect.entityState.isUnchanged());
                        if (result === ChangeAction.DiscardAndContinue) {
                            return this.visionService.rejectChanges(changedVisions);
                        } else if (result === ChangeAction.SaveAndContinue) {
                            return this.visionService.saveEntities(changedVisions);
                        } else if (lastSelectedCategory) { // stay here - reselect last category
                            setTimeout(() => e.component?.selectItem(this.categories!.indexOf(lastSelectedCategory)));
                        }

                        return EMPTY;
                    }),
                    this.takeUntilDestroyed(),
                ).subscribe(() => this.setSelectedCategory.next(newCategorySelection));
            } else {
                this.setSelectedCategory.next(newCategorySelection);
            }
        }
    }

    public onVisionStatementChanged(vision: Vision) {
        if (vision.statement?.trim()) {
            vision.status = VisionStatus.Completed;
        } else {
            vision.status = VisionStatus.InProgress;
        }

        this.checkContentActionMenu();
    }

    @Autobind
    public saveVision(vision: Vision) {
        this.reviewResult = undefined;
        const changedVisions: Vision[] = [vision];
        this.onVisionStatementChanged(vision); // in case status not updated yet (e.g. just accepting previously defined statement)
        if (this.summaryIntegratedVision?.lastPublished) {
            this.summaryIntegratedVision.lastPublished = undefined;
            if (vision !== this.summaryIntegratedVision) {
                changedVisions.push(this.summaryIntegratedVision);
            }
        }

        return this.visionService.saveEntities(changedVisions);
    }

    public moveToNextCategory() {
        // move to the next incomplete category
        const categories = this.categories;
        if (categories?.length && this.selectedCategory) {
            const currentIndex = categories.indexOf(this.selectedCategory);
            let nextIndex = -1;

            for (let i = 1; i < categories.length; i++) {
                const idx = (currentIndex + i) % categories.length;
                const nextVision = this.getEditableVisionForCategory(categories[idx].visionCategoryId);
                if (nextVision && !nextVision.extensions.isCompleted) {
                    nextIndex = idx;
                    break;
                }
            }

            if (nextIndex !== -1) {
                this.categoryListComponent?.instance.selectItem(nextIndex);
            }
        }
    }

    @Autobind
    public discardVision(vision: Vision) {
        this.reviewResult = undefined;
        return this.visionService.rejectChanges(vision);
    }

    @Autobind
    public reviewVisionStatement(vision: Vision) {
        let reviewOps: Observable<string> = of("");
        if (vision.extensions.isPersonal) {
            reviewOps = this.getCategoryDescription(vision.visionCategory).pipe(
                switchMap((categoryDescription) =>
                    this.chatAiService.reviewVisionStatement(vision.visionCategory?.name ?? "", categoryDescription, vision.statement))
            );
        } else if (this.ownersVisions) {
            const ownersVisionStatements = this.ownersVisions
                .map((v) => v.statement)
                .filter((s) => s?.trim());
            reviewOps = this.getCategoryDescription(this.selectedCategory).pipe(
                switchMap((categoryDescription) => this.chatAiService.reviewIntegratedVisionStatement(
                    this.selectedCategory?.name ?? "", categoryDescription, ownersVisionStatements, vision.statement)),
            );
        } else {
            // summary
            const integratedStatements = this.visions
                .filter((v) => v.extensions.isIntegrated && v.visionCategoryId && v.statement?.trim())
                .map((v) => v.statement!);
            reviewOps = this.chatAiService.reviewIntegratedVisionSummary(integratedStatements, vision.statement);
        }

        return reviewOps.pipe(
            tap((result) => this.reviewResult = result),
            this.takeUntilDestroyed(),
        );
    }

    @Autobind
    public integrateOwnersVisions() {
        const ownersVisionStatements = this.ownersVisions!
            .map((v) => v.statement)
            .filter((s) => s?.trim());
        return this.getCategoryDescription(this.selectedCategory).pipe(
            switchMap((categoryDescription) =>
                this.chatAiService.integrateOwnersVisions(this.selectedCategory?.name ?? "", categoryDescription, ownersVisionStatements)),
            tap((result) => this.reviewResult = result),
            this.takeUntilDestroyed(),
        );
    }

    @Autobind
    public generateIntegratedSummary() {
        const integratedStatements = this.visions
            .filter((v) => v.extensions.isIntegrated && v.visionCategoryId && v.statement?.trim())
            .map((v) => v.statement!);
        return this.chatAiService.generateIntegratedVisionSummary(integratedStatements).pipe(
            tap((result) => this.reviewResult = result),
            this.takeUntilDestroyed(),
        );
    }

    public improveClarityGrammar(vision: Vision) {
        return this.chatAiService.improveClarityGrammar(vision.statement).pipe(
            tap((result) => this.reviewResult = result),
            this.takeUntilDestroyed(),
        );
    }

    public getEditableVisionForCategory(categoryId: number) {
        return this.visions.find((v) => (v.visionCategoryId ?? 0) === categoryId && (this.personId ? v.extensions.isPersonal : v.extensions.isIntegrated));
    }

    public replaceVisionStatement(vision: Vision) {
        if (this.reviewResult) {
            const markers = [
                "improved vision statement:",
                "improved statement:",
                "summary of integrated visions:",
                "generated statement:",
            ];
            const source = this.reviewResult;
            const lower = source.toLowerCase();
            let idx = -1;
            let markerLower = "";
            for (const marker of markers) {
                const found = lower.lastIndexOf(marker);
                if (found >= 0) {
                    idx = found;
                    markerLower = marker;
                    break;
                }
            }
            const baseStatement = idx >= 0
                ? source.substring(idx + markerLower.length).trim()
                : source.trim();
            // if there is a > character, baseStatement should be started after that
            const greaterThanIndex = baseStatement.indexOf(">");
            const newBaseStatement = greaterThanIndex >= 0
                ? baseStatement.substring(greaterThanIndex + 1).trim()
                : baseStatement;
            const newStatement = newBaseStatement.replace(/^"+|"+$/g, "");
            vision.statement = newStatement;
            this.reviewResult = undefined;
            this.onVisionStatementChanged(vision);
        }
    }

    public editPersonalVision(personId: number) {
        return this.visionService.editVisionForPerson(personId, this.teamId!, this.selectedCategory?.visionCategoryId, "Back to integrated vision").subscribe();
    }

    private getOwnersVisionsForCategory(categoryId: number) {
        return this.visions.filter((v) => v.visionCategoryId === categoryId && v.extensions.isPersonal);
    }

    private setBusyWhile<T>(op$: Observable<T>): Observable<T> {
        this.aiBusy = true;
        return op$.pipe(finalize(() => (this.aiBusy = false)));
    }

    private checkContentActionMenu() {
        const hasContent = (this.editableVision?.statement?.trim().length ?? 0) > 10;
        this.reviewStatementMenuItem.disabled = !hasContent;
        this.improveClarityGrammarMenuItem.disabled = !hasContent;
    }

    private getCategoryDescription(category?: VisionCategory) {
        if (category?.description) {
            return of(category.description);
        } else if (category?.descriptionSlug) {
            return this.ikService.getArticle(category.descriptionSlug as ImplementationKitArticle).pipe(
                map((a) => StringUtilities.htmlToPlaintext(a.answer) ?? ""),
            );
        } else {
            return of("");
        }
    }

    // scroll to the bottom after getting response from AI so user can see it
    private scrollRightColumnToBottom() {
        const rightColumn = document.getElementById("edit-vision-right-column");
        if (rightColumn) {
            // do this in the next cycle for the AI response to be shown first
            setTimeout(() => rightColumn.scrollIntoView({ behavior: "smooth", block: "end" }));
        }
    }
}

export const EditPersonalVisionPageRoute = new OrganisationPageRouteBuilder<{ teamId: number, personId: number }>()
    .usingNgComponent("adapt-edit-vision-page", EditVisionPageComponent)
    .atOrganisationUrl("teams/:teamId/edit-vision/:personId")
    .verifyingFeatures(FeatureName.CompassVision)
    .withTitle("Edit vision")
    .build();

export const EditIntegratedVisionPageRoute = new OrganisationPageRouteBuilder<{ teamId: number }>()
    .usingNgComponent("adapt-edit-vision-page", EditVisionPageComponent)
    .atOrganisationUrl("teams/:teamId/edit-vision")
    .verifyingFeatures(FeatureName.CompassVision)
    .withTitle("Edit integrated vision")
    .build();
